import { memo, Fragment } from 'react';

import styles from './Marked.module.css';

/**
 * Where `needle` appears in `haystack`, as [start, end) offsets. Every
 * occurrence, non-overlapping, case-folded. The needle is compared whole, not
 * split into words: someone typing "crewman position" is looking for that
 * phrase, and matching the words separately would mark every "position".
 */
function findMarks(haystack: string, needle: string): [number, number][] {
  const term = needle.trim().toLowerCase();
  if (term === '') return [];
  const hay = haystack.toLowerCase();
  const out: [number, number][] = [];
  let at = hay.indexOf(term);
  while (at !== -1) {
    out.push([at, at + term.length]);
    at = hay.indexOf(term, at + term.length);
  }
  return out;
}

/**
 * Text with the searched-for words picked out.
 *
 * One component for every place a match is shown — a row's name, its posting
 * chip, a result row's snippet — because the mark is what connects them: the
 * row you clicked and the message you land on have to be recognisably the same
 * thing. It renders no wrapper, so it drops into whatever span is already
 * clamping or styling the line.
 *
 * Deliberately NOT a `<mark>` element: the browser's default yellow fill and
 * ink is exactly the pairing the tokens exist to replace.
 */
export const Marked = memo(function Marked({ text, term }: { text: string; term: string }) {
  const marks = findMarks(text, term);
  if (marks.length === 0) return <>{text}</>;

  const out = [];
  let at = 0;
  for (const [start, end] of marks) {
    if (start > at) out.push(<Fragment key={`t${at}`}>{text.slice(at, start)}</Fragment>);
    out.push(
      <span key={`m${start}`} className={styles.mark}>
        {text.slice(start, end)}
      </span>,
    );
    at = end;
  }
  if (at < text.length) out.push(<Fragment key={`t${at}`}>{text.slice(at)}</Fragment>);
  return <>{out}</>;
});
